import {Injectable} from '@angular/core';
import {forkJoin, from, map, Observable, of, Subject} from 'rxjs';
import {IWidgetModelBase, IWidgetModelCreateResponse, WidgetKeyModel, WidgetModel} from '../model/widget.model';
import {ConnectorService} from '../../../services/api/connector.service';
import {CrmService} from '../../../services/api/crm.service';
import {BoardTableModel} from '../../../models/tasktable.model';
import {TaskServiceService} from '../../../services/task-service.service';

@Injectable({
    providedIn: 'root'
})
export class WidgetsService {
    public subWidgetSettingsChanged = new Subject<WidgetModel>()
    public subWidgetNewCreate = new Subject<boolean>()
    public subWidgetNewHasCreated = new Subject<WidgetModel>()
    public subShowWidgets = new Subject<boolean>()

    constructor(
        private connectionService: ConnectorService,
        private crmService: CrmService,
        private taskService: TaskServiceService
    ) {
    }

    getWidgetSettings(widget: WidgetModel): Observable<WidgetModel> {
        switch (widget.widget_key) {
            case 'WIDGET_CRM_TASKS':
                return forkJoin([
                    this.getAllBoardsRx(widget),
                    this.crmService.getUserStatusOrPropertiesSettings('tasks_all_boards')
                ]).pipe(map(res => {
                    widget.content.dashes = res[0]
                    widget.content.colonsSettings = res[1].setting;
                    return widget;
                }))
            case 'WIDGET_CRM_BOARD':
                widget.options_dict? null :  widget.options_dict= {alias: ''};
                return this.getAllBoardsRx(widget).pipe(map(res => { 
                   widget.content? widget.content.dashes = res   :  widget.content = {dashes : res }; 
                    return widget;
                }))
            case 'WIDGET_CRM_TASK_CALENDAR':
                return this.getAllBoardsRx(widget).pipe(map(res => { 
                   widget.content? widget.content.dashes = res   :  widget.content = {dashes : res }; 
                    return widget;
                }))
            case 'WIDGET_CRM_TASK_FILTER':
                return this.getAllBoardsRx(widget).pipe(map(res => {  
                   widget.content? widget.content.dashes = res   :  widget.content = {dashes : res }; 
                   return widget;
                }))
            default:
                return of(widget)
        }
    }

    getAllBoardsRx(widget: WidgetModel): Observable<BoardTableModel[]> {
        if(widget.content?.dashes) {
            return of(widget.content.dashes as unknown as BoardTableModel[])
        } else {
            return from(this.taskService.getAllBoards(false))
            .pipe(map(result => result.boards.concat(result.groups.flatMap(el => el.boards)).sort((a, b) => a.alias.localeCompare(b.alias))))
        }
    }

    /**
     * Получение всех виджетов пользователя
     */
    getWidgets(): Observable<{ widgets: WidgetModel[] }> {
        return this.connectionService.get('instance/widget/options/get_all_widgets');
    }

    /**
     * Редактирование виджета
     * @param widgetId
     * @param widgetData
     */
    updateWidget(
        widgetId: number,
        widgetData: {
            title: string,
            height: number,
            width: number,
            screen_x: number,
            screen_y: number
        }
    ): Observable<any> {
        return this.connectionService.put('instance/widget/update/' + widgetId, widgetData);
    }

    /**
     * Получение всех ключей для нового виджета по его типу
     * @param widgetType
     */
    getWidgetsKeys(widgetType: string): Observable<{ widget: WidgetKeyModel[] }> {
        return this.connectionService.get('instance/widget/keys', {widget_type: widgetType});
    }

    /**
     * Создание виджета
     * @param data
     */
    createWidget(
        data: {
            title: string,
            height: number,
            width: number,
            screen_x: number,
            screen_y: number,
            widget_key: string | null,
            widget_type: string,
        }
    ): Observable<IWidgetModelCreateResponse> {
        return this.connectionService.post('instance/widget/create', data);
    }

    /**
     * Обновление настроек виджета
     * @param widgetId
     * @param options
     */
    changeWidgetOptions(
        widgetId: number,
        options: any
    ): Observable<{ message: string, widget: WidgetModel }> {
        return this.connectionService.put('instance/widget/options/' + widgetId, options, false);
    }

    /**
     * Удаление виджета по ID
     * @param widgetId
     */
    deleteWidget(widgetId: number): Observable<any> {
        return this.connectionService.delete('instance/widget/' + widgetId);
    }

    /**
     * Получение виджета по ID
     * @param widgetId
     * @param pagination
     */
    getWidgetById(
        widgetId: number,
        pagination?: { current_page: number, limit: number }
    ): Observable<{ widget: WidgetModel }> {
        return this.connectionService.get('instance/widget/' + widgetId, pagination);
    }
}
